import type { ItineraryActivity, ItineraryDay, ItineraryGuest } from 'voyager';

export type PackageItineraryEntryKind = 'checkIn' | 'booked' | 'pending' | 'checkOut';

export interface PackageItineraryEntry {
  kind: PackageItineraryEntryKind;
  date: string;
  time?: string;
  title: string;
  location?: string;
}

export interface PackageItineraryGuest {
  id: string;
  name: string;
  entries: PackageItineraryEntry[];
}

const KIND_ORDER: Record<PackageItineraryEntryKind, number> = {
  checkIn: 0,
  booked: 1,
  pending: 2,
  checkOut: 3,
};

const compareEntries = (a: PackageItineraryEntry, b: PackageItineraryEntry) =>
  a.date.localeCompare(b.date) ||
  KIND_ORDER[a.kind] - KIND_ORDER[b.kind] ||
  (a.time ?? '').localeCompare(b.time ?? '');

const toActivity = (entry: PackageItineraryEntry, index: number): ItineraryActivity => ({
  id: `${entry.kind}-${entry.date}-${index}`,
  time: entry.time,
  title: entry.title,
  location: entry.location,
  pending: entry.kind === 'pending',
});

export const buildPackageItinerary = (
  guests: PackageItineraryGuest[],
  sharedEntries: PackageItineraryEntry[],
  formatDate: (isoDate: string) => string
): ItineraryGuest[] =>
  guests.map(({ id, name, entries }) => ({
    id,
    name,
    days: [...entries, ...sharedEntries]
      .sort(compareEntries)
      .reduce<ItineraryDay[]>((days, entry, index) => {
        const date = formatDate(entry.date);
        const lastDay = days[days.length - 1];
        if (lastDay?.date === date) {
          lastDay.activities.push(toActivity(entry, index));
        } else {
          days.push({ date, activities: [toActivity(entry, index)] });
        }
        return days;
      }, []),
  }));
